import { Download, RefreshCw, Search } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardTransaction } from "../types/card-movements.types";
import { visibleCardMovements } from "../utils/cardMovementHelpers";
import { CardMovementRow } from "./CardMovementRow";

type CardMovementsProps = {
  movements: CardTransaction[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onRowClick?: (transactionId: string) => void;
  onRefresh?: () => void;
  /** Movements that just arrived by socket. */
  highlightedIds?: ReadonlySet<string>;
};

export const CardMovements = ({
  movements,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  onRowClick,
  onRefresh,
  highlightedIds,
}: CardMovementsProps) => {
  const { t } = useTranslations("cards");

  // Derived, not stateful: `CardDetailPage` paginates with `movements.length` over the unfiltered
  // list, so dropping rows here leaves the `skip` cursor agreeing with the backend.
  const rows = useMemo(() => visibleCardMovements(movements), [movements]);

  return (
    // Laid out against its own width (`@container`): next to the 380px sidebar this column is narrow on a
    // laptop too, not only on a phone, and the fixed status and amount columns left the merchant no room.
    <section className="@container overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      {/* Header: search + export */}
      <div className="flex flex-col gap-2 border-b border-border p-3 @md:flex-row @md:items-center @md:justify-between @md:gap-3">
        <div className="relative w-full @md:max-w-xs @md:flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            disabled
            placeholder={t("filters_search_alias_placeholder")}
            className="h-8 w-full rounded-[var(--radius-md)] border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          />
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <ColocaButton
              variant="default"
              size="sm"
              icon={RefreshCw}
              onClick={onRefresh}
              loading={isLoading}
            >
              {t("movements_refresh")}
            </ColocaButton>
          )}
          <ColocaButton variant="default" size="sm" icon={Download} disabled>
            {t("card_detail_export")}
          </ColocaButton>
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-border">
        {isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <span className="text-sm text-muted-foreground">{t("movements_loading")}</span>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">{t("movements_empty")}</div>
        ) : (
          rows.map((tx) => (
            <CardMovementRow
              key={tx.id}
              movement={tx}
              onClick={onRowClick}
              highlighted={highlightedIds?.has(tx.id)}
            />
          ))
        )}
      </div>

      {/* Load more */}
      {hasMore && !isLoading && onLoadMore && (
        <div className="border-t border-border p-4 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t("movements_load_more")}
          </button>
        </div>
      )}

      {isLoading && rows.length > 0 && (
        <div className="border-t border-border p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>{t("movements_loading_more")}</span>
          </div>
        </div>
      )}
    </section>
  );
};
